import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useGetDefaultTenantTimeRange } from '@modules/common/hooks';
import {
  decompressURLSearchParams,
  getDateTimeRangeFromTimestamp,
  isEmptyWhereClause,
} from '@modules/common/utils';
import { populatePayloadFromSearchParams } from '@modules/common/utils/populatePayloadFromSearchParams';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { FeedActionType } from '@modules/feed/store/searchActionType';
import { FeedDashboardFilters } from '@modules/feed/store/types';
import { getSearchQueryFromMetadata } from '@modules/feed/utils';
import { GetUseInSearchLinkOptions } from '@modules/feed/utils/getUseInSearchLink';
import { parseStringifiedDuration } from '@modules/feed/utils/parseStringifiedDuration';
import { useEffect } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';

export const useInitSearchStoreFromSearchParams = () => {
  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const metadata = useAnalysisStore(({ metadata }) => metadata);
  const eqlProperties = useAnalysisStore(
    ({ context }) => context.eql?.properties,
  );
  const defaultTimeRange = useGetDefaultTenantTimeRange();

  const areDashboardFiltersPresentInStore = useFeedStore(
    (state) => !!state.visual.dashboardFilters,
  );

  useEffect(() => {
    const queryPayload: Partial<GetUseInSearchLinkOptions> = {};
    populatePayloadFromSearchParams(
      queryPayload,
      [
        'duration',
        'where',
        'from',
        'to',
        'whereClauseConfig',
        'prompt',
        'whereClause',
      ],
      decompressURLSearchParams,
    );

    const dashboardFiltersPayload: Partial<FeedDashboardFilters> = {};

    populatePayloadFromSearchParams(
      dashboardFiltersPayload,
      { duration: 'dashboardDuration', whereClause: 'dashboardWhereClause' },
      decompressURLSearchParams,
    );

    if (dashboardFiltersPayload.duration) {
      dashboardFiltersPayload.duration = parseStringifiedDuration(
        dashboardFiltersPayload.duration,
      );
    }

    if (
      dashboardFiltersPayload.whereClause ||
      dashboardFiltersPayload.duration
    ) {
      dispatch({
        type: FeedActionType.SET_VISUAL,
        payload: {
          dashboardFilters: {
            whereClause: dashboardFiltersPayload.whereClause,
            duration: dashboardFiltersPayload.duration,
          },
        },
      });
    }

    if (queryPayload.duration) {
      queryPayload.duration = parseStringifiedDuration(queryPayload.duration);
      dispatch({
        type: FeedActionType.SET_QUERY,
        payload: { duration: queryPayload.duration },
      });
    }

    if (queryPayload.from && queryPayload.to) {
      queryPayload.duration = getDateTimeRangeFromTimestamp(
        parseInt(queryPayload.from, 10),
        parseInt(queryPayload.to, 10),
      );

      dispatch({
        type: FeedActionType.SET_QUERY,
        payload: {
          duration: queryPayload.duration,
        },
      });
    }

    if (queryPayload.whereClauseConfig || queryPayload.whereClause) {
      const feedWhereClause = getSearchQueryFromMetadata({
        metadata: metadata,
        getDurationField: false,
        defaultTimeRange,
        eqlProperties,
        sourceDetailsMap,
      }).whereClause;

      const isFeedWhereClauseEmpty = feedWhereClause
        ? isEmptyWhereClause(feedWhereClause)
        : true;

      const queryWhereClause =
        queryPayload.whereClauseConfig?.whereClause ?? queryPayload.whereClause;

      const combinedWhereClause =
        isFeedWhereClauseEmpty || !feedWhereClause || !queryWhereClause
          ? queryWhereClause
          : combineWhereClauses(
              feedWhereClause,
              queryWhereClause,
              OperationType.AND,
            );

      dispatch({
        type: FeedActionType.SET_QUERY,
        payload: {
          whereClause: combinedWhereClause,
        },
      });
    }

    if (queryPayload.where) {
      dispatch({
        type: FeedActionType.SET_QUERY,
        payload: {
          whereClause: queryPayload.where,
        },
      });
    }

    if (queryPayload.prompt) {
      dispatch({
        type: FeedActionType.SET_PROMPT,
        payload: queryPayload.prompt,
      });
    }
  }, [defaultTimeRange, dispatch, eqlProperties, metadata, sourceDetailsMap]);

  const searchParams = new URLSearchParams(
    decompressURLSearchParams(window.location.search),
  );

  const areDashboardFiltersPresentInURL =
    searchParams.has('dashboardDuration') ||
    searchParams.has('dashboardWhereClause');

  const shouldResetDashboardFiltersInStore =
    !areDashboardFiltersPresentInURL && areDashboardFiltersPresentInStore;

  // Reset the dashboard filters if URL params are removed/changed
  useEffect(() => {
    if (shouldResetDashboardFiltersInStore) {
      dispatch({
        type: FeedActionType.SET_VISUAL,
        payload: {
          dashboardFilters: null,
        },
      });
    }
  }, [dispatch, shouldResetDashboardFiltersInStore]);
};
